import { Bell, Check, DeviceMobile, LinkSimple, Pulse, X } from "@phosphor-icons/react";
import { QRCodeSVG } from "qrcode.react";
import { type ReactNode, useEffect, useState } from "react";
import { ThriveLeaf } from "../brand/thrive-leaf";
import { interactionSoundProps } from "../interaction-sound";
import { cn } from "../lib/cn";
import { Modal, ModalTitle } from "../primitives/modal";

/**
 * CompanionDownloadModal, the post-onboarding nudge to install the mobile app.
 *
 * A plain modal on `/today`, deliberately NOT a Heartbeat attention card: the
 * rail stays fully Heartbeat-driven. It opens once after onboarding (gated by
 * a once-only, cross-device dismissed flag) and never touches the attention
 * cooldown or daily cap.
 *
 * Two columns: an editorial left panel (what the app is for) and a dark teal
 * right panel with a platform toggle, QR code, and store link. The user picks
 * iOS or Android and scans with their phone; desktop users click the badge.
 */

export interface CompanionAppLinks {
  /** App Store URL. */
  ios: string;
  /** Google Play URL. */
  android: string;
}

export interface CompanionDownloadModalProps {
  open: boolean;
  /** Store URLs encoded into the QR codes and the badge links. */
  links: CompanionAppLinks;
  /** Fired on close (overlay, escape, X, "Maybe later"); persists the dismissal. */
  onDismiss: () => void;
  /**
   * Which platforms to offer. Defaults to both. Pass a single platform (e.g.
   * `["ios"]` for the Apple Health connect flow, which is iPhone-only) to hide
   * the toggle and lock the QR + badge to that store.
   */
  platforms?: ReadonlyArray<Platform>;
}

type Platform = "ios" | "android";

const FEATURES: ReadonlyArray<{ icon: ReactNode; title: string; body: string }> = [
  {
    icon: <Bell size={18} weight="regular" />,
    title: "Timely by design",
    body: "One nudge the moment it matters, never a pile of notifications.",
  },
  {
    icon: <Pulse size={18} weight="regular" />,
    title: "Reads your signals",
    body: "Sleep, steps and heart rate sync on their own in the background.",
  },
  {
    icon: <Check size={18} weight="regular" />,
    title: "One tap to log",
    body: "Tell the Companion how a day landed in a couple of seconds.",
  },
];

const PLATFORM_META: Record<Platform, { label: string; cta: string; requirement: string }> = {
  ios: { label: "iOS", cta: "Get the iOS app", requirement: "iPhone · iOS 16 or later" },
  android: { label: "Android", cta: "Get the Android app", requirement: "Android 10 or later" },
};

export function CompanionDownloadModal({
  open,
  links,
  onDismiss,
  platforms = ["ios", "android"],
}: CompanionDownloadModalProps) {
  const [platform, setPlatform] = useState<Platform>(platforms[0] ?? "ios");
  const showToggle = platforms.length > 1;

  // The initial useState above only runs on mount. This instance is never
  // unmounted between opens (e.g. the connectors page's single MobileAppQrModal
  // reused for both Apple Health and Health Connect), so without this, the
  // platform picked for the *first* caller sticks forever - a Health Connect
  // open right after an Apple Health one would still show "Get the iOS app".
  // Compared by content (platforms.join), not array identity: a caller that
  // never passes `platforms` gets a fresh default array on every render, and
  // resetting on that unstable identity would stomp a member's manual toggle
  // choice on unrelated re-renders while the modal is open.
  const platformsKey = platforms.join(",");
  // biome-ignore lint/correctness/useExhaustiveDependencies: platformsKey is a deliberate content-based proxy for platforms
  useEffect(() => {
    setPlatform(platforms[0] ?? "ios");
  }, [platformsKey]);

  const storeUrl = platform === "ios" ? links.ios : links.android;
  const meta = PLATFORM_META[platform];

  // Paper-on-dark-teal ink for the right panel, matching the attention card.
  const panelInk = (pct: number) => `color-mix(in srgb, var(--color-paper) ${pct}%, transparent)`;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        if (!next) onDismiss();
      }}
      size="2xl"
      contentClassName="overflow-hidden p-0"
    >
      <ModalTitle className="sr-only">Download the HealthOS companion app</ModalTitle>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_minmax(0,460px)]">
        {/* Left: editorial. */}
        <div className="flex flex-col gap-6 bg-cream-warm px-8 py-9 sm:px-10">
          <span className="inline-flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-teal-700">
            <span aria-hidden="true" className="h-[6px] w-[6px] rounded-full bg-teal-600" />
            HealthOS · Companion app
          </span>

          <h2 className="m-0 text-balance font-title text-[40px] font-medium leading-[1.05] tracking-[-0.02em] text-navy">
            The Companion goes <em className="font-title text-teal-700 italic">where you go.</em>
          </h2>

          <p className="m-0 max-w-[46ch] text-base leading-[1.6] text-ink-secondary">
            Your most important moments don't happen at a desk. They happen at the kitchen counter,
            on the evening walk, in the minutes before sleep. The app is how HealthOS reaches you{" "}
            <strong className="font-semibold text-navy">in time</strong>, and the single biggest
            thing you can do to get more from your plan.
          </p>

          <div className="h-px w-full bg-mist" />

          <ul className="m-0 flex list-none flex-col gap-5 p-0">
            {FEATURES.map((f) => (
              <li key={f.title} className="flex items-start gap-3.5">
                <span
                  aria-hidden="true"
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-teal-50 text-teal-700"
                >
                  {f.icon}
                </span>
                <div className="min-w-0">
                  <p className="m-0 font-sans text-base font-semibold text-navy">{f.title}</p>
                  <p className="m-0 mt-0.5 text-sm leading-[1.5] text-ink-secondary">{f.body}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Right: dark teal panel with toggle + QR + store link. */}
        <div className="p-4 sm:p-5">
          <div
            className="relative flex h-full flex-col items-center gap-6 overflow-hidden rounded-3xl px-6 py-7"
            style={{
              background:
                "linear-gradient(165deg, var(--color-attention-surface-1) 0%, var(--color-attention-surface-2) 55%, var(--color-attention-surface-3) 100%)",
            }}
          >
            <button
              type="button"
              onClick={onDismiss}
              aria-label="Dismiss"
              {...interactionSoundProps("dismiss")}
              className="absolute right-4 top-4 grid h-8 w-8 place-items-center rounded-full [transition:background-color_200ms_var(--ease-out-expo),transform_120ms_var(--ease-out-expo)] active:scale-[0.92]"
              style={{ background: panelInk(14), color: panelInk(85) }}
            >
              <X size={16} weight="bold" />
            </button>

            {showToggle ? (
              <div
                className="inline-flex rounded-full p-1"
                role="tablist"
                aria-label="Choose a platform"
                style={{ background: panelInk(12) }}
              >
                {platforms.map((p) => {
                  const selected = platform === p;
                  return (
                    <button
                      key={p}
                      type="button"
                      role="tab"
                      aria-selected={selected}
                      onClick={() => setPlatform(p)}
                      // A "select" tick when switching platform; the already
                      // selected tab stays silent (re-clicking it is a no-op).
                      {...interactionSoundProps(selected ? "none" : "select")}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full px-5 py-2 font-sans text-sm font-semibold tracking-[-0.005em] [transition:background-color_200ms_var(--ease-out-cubic),color_200ms_var(--ease-out-cubic)]",
                        selected
                          ? "bg-paper text-navy shadow-card ring-2 ring-teal-400"
                          : "bg-transparent",
                      )}
                      style={selected ? undefined : { color: panelInk(78) }}
                    >
                      <DeviceMobile size={15} weight={selected ? "fill" : "regular"} />
                      {PLATFORM_META[p].label}
                    </button>
                  );
                })}
              </div>
            ) : null}

            <div className="relative grid place-items-center rounded-2xl bg-white p-5 shadow-card">
              <QRCodeSVG
                value={storeUrl}
                size={188}
                marginSize={0}
                level="H"
                title={`QR code to download the Thrive ${meta.label} app. Scan with your phone camera.`}
              />
              <span
                aria-hidden="true"
                className="absolute grid h-12 w-12 place-items-center rounded-full bg-white"
              >
                <ThriveLeaf className="h-7 w-7" />
              </span>
            </div>

            <p
              className="m-0 text-center font-mono text-xs uppercase tracking-[0.18em]"
              style={{ color: panelInk(70) }}
            >
              Point your phone's camera at the code
            </p>

            <a
              href={storeUrl}
              target="_blank"
              rel="noreferrer noopener"
              {...interactionSoundProps("action")}
              className="inline-flex w-full max-w-[280px] items-center justify-center gap-2 whitespace-nowrap rounded-full bg-paper px-5 py-3.5 font-sans text-base font-semibold text-navy tracking-[-0.005em] shadow-card [transition:transform_120ms_var(--ease-out-cubic)] hover:-translate-y-px active:translate-y-0 active:scale-[0.97]"
            >
              {meta.cta}
              <LinkSimple size={16} weight="bold" />
            </a>

            <p
              className="m-0 font-mono text-xs uppercase tracking-[0.16em]"
              style={{ color: panelInk(55) }}
            >
              {meta.requirement}
            </p>
          </div>
        </div>
      </div>

      {/* Full-width footer. */}
      <footer className="flex items-center justify-between gap-4 border-t border-mist bg-cream-warm px-8 py-4 sm:px-10">
        <span className="font-mono text-xs uppercase tracking-[0.16em] text-gray-400">
          Private by default · No noise you didn't ask for
        </span>
        <button
          type="button"
          onClick={onDismiss}
          {...interactionSoundProps("dismiss")}
          className="shrink-0 rounded-full border border-mist-dark bg-transparent px-5 py-2.5 font-sans text-base font-medium text-ink-secondary tracking-[-0.005em] [transition:background-color_200ms_var(--ease-out-cubic)] hover:bg-mist"
        >
          Maybe later
        </button>
      </footer>
    </Modal>
  );
}
